'use strict';

import React, {Component} from 'react';
import PropTypes from "prop-types";
import {observer} from "mobx-react";
import classnames from "classnames";

import Form from 'react-bootstrap/Form';
import Alert from "react-bootstrap/Alert";

import {capitalize, guid} from "@uw-it-sis/lib-react/lib/Utils";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";

import optionalChargesStore from "./OptionalChargesStore";
import {Campus} from '@uw-it-sis/lib-react/lib/AppConstants';
import Button from "react-bootstrap/Button";


const gsGAIP = <a href='http://www.washington.edu/admin/hr/benefits/insure/gaip/' target='_blank'>Graduate Appointee Insurance Program</a>;
const gsSASIP = <a href='http://www.washington.edu/ship/international-student-insurance-health-plan/' target='_blank'>International Student Health Insurance Plan</a>;

class OpChargeInsurance extends Component {
    constructor(props) {
        super(props);
        this.props = props;
        this.store = optionalChargesStore;
    }

    componentDidMount() {
        this.store.updateFormData('CMPS', this.store.data.branch_code);
    }

    handleChange = (e) => {
        const {name, value} = e.target;
        this.store.updateFormData(name, value);
    }

    submitForm = (e) => {
        e.preventDefault();
        this.props.onSuccess();
    }

    insuranceCancelledDescription = () => {
        let component;
        const data = this.store.data;
        if (data.ins_update_sw === "Y") {
            component =
                <p><strong>You are currently not eligible for the {gsSASIP}.</strong></p>
        } else {
            component =
                <p>You are not enrolled in the {gsSASIP} for {termName} because you did not meet the eligibility requirement of remaining registered through the 3rd Friday of the quarter.</p>
        }

        return component;
    }

    insuranceCurrentDescription = () => {
        let component = [];
        const data = this.store.data;
        const annualQtrCode = parseInt(data.ins_annual_qtr);
        const annualYear = parseInt(data.ins_annual_year);
        let currentSelection = parseInt(data.insurance_sel);
        const statusChar = data.ins_status_char;
        const summerYear = annualQtrCode === 4 ? annualYear + 1 : annualYear;
        const activeQtr = parseInt(this.props.termId.substring(4, 5));
        const termName = TermUtils.makeTermDescriptionFromTermId(this.props.termId);

        if (statusChar === "W") {
            if (annualQtrCode > 0) {
                component.push(
                    <React.Fragment key={guid()}>Your waiver of the {gsSASIP} is in effect through summer quarter {summerYear}.</React.Fragment>
                )
            } else {
                component.push(
                    <React.Fragment key={guid()}>Your waiver of the {gsSASIP} is in effect for {termName}.</React.Fragment>
                )
            }

            return component;
        }

        if (annualQtrCode > 0) {
            if (currentSelection > 3) {
                currentSelection -= 4;
            }

            component.push(
                <React.Fragment key={guid()}>You have {statusChar === "A" && annualQtrCode !== activeQtr ? "already":""} selected an annual {gsSASIP} through summer quarter {summerYear}: {this.insuranceCoverageText(currentSelection)}</React.Fragment>
            )
        } else {
            component.push(
                <React.Fragment key={guid()}>{this.insuranceSelectionText(currentSelection)}</React.Fragment>
            )
        }

        if (Number(data.curr_ins_sel_amt) > 0) {
            component.push(
                <React.Fragment key={guid()}>{` - $${Number(data.curr_ins_sel_amt).toFixed(2)}`}</React.Fragment>
            )
        }

        if (annualQtrCode > 0 && annualQtrCode !== activeQtr) {
            component.push(
                <React.Fragment key={guid()}> (selected {TermUtils.TERMS[annualQtrCode]} quarter {annualYear})</React.Fragment>
            )
        }

        component.push(
            <React.Fragment key={guid()}>.</React.Fragment>
        )

        return component;
    }

    insuranceSelectionText = (selectCode) => {
        switch (parseInt(selectCode)) {
            case 0:
                return this.insuranceCoverageText(selectCode);
            case 1:
            case 2:
            case 3:
            case 4:
            case 9:
            case 10:
            case 11:
            case 12:
                //...Quarter-only insurance
                return `${TermUtils.TERMS[this.store.data.qtr]} quarter ${this.store.data.year}: ${this.insuranceCoverageText(selectCode)}`;
            case 5:
            case 6:
            case 7:
            case 8:
            case 13:
            case 14:
            case 15:
            case 16:
                //...Annual insurance through summer
                const summerYear = this.store.data.qtr === 4 ? parseInt(this.store.data.year) + 1 : this.store.data.year;
                return `Annual through summer quarter ${summerYear}: ${this.insuranceCoverageText(selectCode)}`

            default:
                return "";
        }
    }

    insuranceCoverageText = (selectCode) => {
        let numDependents = parseInt(this.store.data.ins_num_dep_u21) + parseInt(this.store.data.ins_num_dep_21);
        switch (selectCode) {
            case 0:
                return "No insurance";

            case 1:
            case 5:
            case 9:
            case 13:
                return "Student only";

            case 2:
            case 6:
            case 10:
            case 14:
                return "Student & spouse";

            case 3:
            case 7:
                return "Student, spouse and " + numDependents + (numDependents === 1 ? " child" : " children");

            case 11:
            case 15:
                return "Student, spouse & children";

            case 4:
            case 8:
                return "Student with " + numDependents + (numDependents === 1 ? " child" : " children");

            case 12:
            case 16:
                return "Student & children";

            default:
                return "Unknown (" + selectCode + ")";
        }
    }

    insuranceDisplayUpdateButtons = () => {
        let component;
        const data = this.store.data;
        const formData = this.store.formData

        let numInsMenus = parseInt(data.num_ins_menus);

        if (numInsMenus === 0) {
            return null;
        }
        let currentSelection = -1;
        if (formData.INS.trim()) {
            currentSelection = parseInt(formData.INS);
        }

        let quarterlyText = `${this.props.termName} insurance`;
        let baseAmount = data.ins_menu_amt$1;
        let adminFeeAmount = data.ins_admin_fee;
        let brokerFeeAmount = data.ins_broker_fee;

        let annualYear = (parseInt(data.qtr) === 4) ? parseInt(data.year) + 1 : parseInt(data.year);
        let annualText = `Annual insurance through summer quarter ${annualYear}`;
        let annualAmount = data.ins_menu_amt$5;

        let numQuarters = parseInt(data.qtr) === 1 ? 3 : (parseInt(data.qtr) === 3 ? 1 : parseInt(data.qtr));

        let u21options = ["None", "1", "2", "3 or more"].map((option, i) => {
            return <option key={i} value={i}>{option}</option>;
        });
        let numO21options = 10
        let o21options = Array.from({length: numO21options}, (_, i) => i + 1).map((option, i) => {
            return <option key={i} value={option}>{option}</option>;
        })

        component.push(
            <React.Fragment key={guid()}>
                <span id="spanInsuranceInfo">Cost:
                    <span id="spanInsuranceCost"
                          data-base-cost={baseAmount}
                          data-broker-fee={brokerFeeAmount} data-admin-fee={adminFeeAmount}>
                        ${Number(baseAmount).toFixed(2)}
                    </span> --
                    <span id="spanInsuranceText" data-text={quarterlyText}>
                        {currentSelection > 4 ? annualText : quarterlyText}
                    </span>
                </span>
                <Form onSubmit={this.submitForm}>
                    <div>
                        <div className={classnames({"d-none": parseInt(data.qtr) === 3})}>
                            <Form.Check
                                id="chkAnnual"
                                label={annualText}
                                name="INSANNL"
                                type="checkbox"
                                value="Y"
                                defaultChecked={formData.INSANNL === "Y"}
                                onChange={this.handleChange}
                                data-num-quarters={numQuarters}
                            />
                        </div>
                        <div>
                            <Form.Check
                                id="chkDependents"
                                label="Include dependents in insurance plan"
                                name="INSDEPS"
                                type="checkbox"
                                value="Y"
                                defaultChecked={formData.INSDEPS === "Y"}
                                onChange={this.handleChange}
                            />
                        </div>
                        <div className={classnames({"d-none": true})}>
                            <div>
                                <Form.Check
                                    id="chkSpouse"
                                    label="Include spouse in insurance plan"
                                    name="INSSPOUSE"
                                    type="checkbox"
                                    value="Y"
                                    defaultChecked={formData.INSSPOUSE === "Y"}
                                    onChange={this.handleChange}
                                />
                            </div>
                            <div>
                                <Form.Group controlId="selDependentsU21">
                                    <Form.Label>Number of children under the age of 21:</Form.Label>
                                    <Form.Select name="INSDEPSU21" defaultValue={formData.INSDEPSU21}>
                                        {u21options}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group controlId="selDependents21">
                                    <Form.Label>Number of children 21 and over:</Form.Label>
                                    <Form.Select name="INSDEPS21" defaultValue={formData.INSDEPS21}>
                                        {o21options}
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        </div>
                    </div>
                </Form>
            </React.Fragment>
        )
    }

    render() {
        const campus = this.props.campus;
        const campusName = capitalize(campus);
        const termName = TermUtils.makeTermDescriptionFromTermId(this.props.termId);
        const data = this.store.data;
        const isInternationalStudent = (data.hasOwnProperty("resident_code") && parseInt(data.resident_code) > 0);
        const isAcademicYearGAIP = (data.hasOwnProperty("acad_year_gaip_sw") && data.acad_year_gaip_sw === "Y");
        const tuitionDueDate = capitalize(data.tuit_due_dt_text);
        const coverageEndDate = capitalize(data.cover_end_dt_text);

        let component = [];

        if (isInternationalStudent) {
            let headingSuffix = campus !== Campus.seattle ? `- UW ${campusName}`:'';
            component.push(
                <h4 className="h2" key={guid()}>International Student Health Insurance Plan (ISHIP) {headingSuffix}</h4>
            );
        }

        if ((parseInt(data.class_code) === 6) || ((parseInt(data.exemption_code) >= 5) && (parseInt(data.exemption_code) <= 9))) {
            if (!isInternationalStudent) {
                component.push(
                    <h4 className="h2" key={guid()}>Student Health Insurance</h4>
                );
            }

            component.push(
                <p key={guid()}>
                    Non-matriculated students, ACCESS program students or students attending through the Tuition Exemption
                    Program for faculty, staff, State of Washington Employees, and Washington State National Guard members,
                    are not eligible to enroll in the {gsSASIP}.
                </p>
            );

            if (this.props.showButton) {
                component.push(<Button onClick={this.props.onSuccess} key={guid()}>Continue</Button>);
            }

            return component;
        }

        if (!isInternationalStudent) {
            if (parseInt(data.class_code) === 8) {
                component.push(
                    <p key={guid()}>
                        If you have a qualifying TA/RA/SA or fellow/trainee appointment for {termName},
                        you may be eligible for the Graduate Appointee Insurance Program (GAIP). The student health
                        insurance plan termination will not affect this coverage. Please see the {gsGAIP} web page
                        for more information.
                    </p>
                )

                if (isAcademicYearGAIP) {
                    component.push(
                        <p key={guid()}>
                            It appears you have participated in the {gsGAIP} for the preceding three quarters and are
                            therefore eligible for UW-paid GAIP insurance for summer quarter, regardless of your student
                            and/or employment status.
                        </p>
                    );
                }
            }

            if (component.length > 0) {
                if (this.props.showButton) {
                    component.push(<Button onClick={this.props.onSuccess} key={guid()}>Continue</Button>);
                }

                return (
                    <React.Fragment key={guid()}>
                        <h4 className="h2">Student Health Insurance</h4>
                        {component}
                    </React.Fragment>
                );
            } else {
                return null;
            }
        }

        if (data.has_annual_ins_sw === "Y") {
            this.store.updateFormData("INS", "0");
        } else {
            this.store.updateFormData("INS", (parseInt(data.curr_ins_sel_code) > 0 ? data.curr_ins_sel_code : "1"));
        }

        let extendCoverage, cancelPolicy;
        if (parseInt(data.qtr) !== 3) {
            extendCoverage = `select the annual plan (extends coverage to ${coverageEndDate}) or `;
            cancelPolicy =
                <li>
                    Once you select the annual plan, no changes or cancellations can be made after {tuitionDueDate} unless you
                    marry or have a child.
                </li>
        }

        let moreInfoLocation;
        switch (campus) {
            case Campus.seattle:
                moreInfoLocation = "the International Student Health Insurance Office, 187B Hall Health Center, email: stdins@uw.edu, phone: 206-543-6202.";
                break;
            case Campus.bothell:
                moreInfoLocation = "International Student Services, Husky Hall, Room 1212, email: iss@uwb.edu, phone 425-352-3876.";
                break;
            case Campus.tacoma:
                moreInfoLocation = "International Student Services, MAT 203, email: uwtiss@uw.edu, phone: 253-692-4762.";
                break;
        }

        component.push(
            <React.Fragment key={guid()}>
                <p>
                    The UW international student health insurance plan (ISHIP) is mandatory for all international students with
                    F-1 and J-1 visa status. Students can choose whether to enroll their spouse and children. For more information
                    on the plan and eligibility, see the {gsSASIP} web page.
                </p>
                <ul>
                    <li>
                        <strong>Eligible international students will be enrolled automatically in quarterly/student-only coverage
                            for each quarter they register for classes.</strong>
                    </li>
                    <li>
                        You can change your coverage plan at any time up to the third Friday of the quarter ({tuitionDueDate} -
                        the tuition due date).
                    </li>
                    <li>
                        You may {extendCoverage} add a spouse or children up to the {tuitionDueDate} tuition due date deadline.
                    </li>
                    {cancelPolicy}
                    <li>
                        If you marry or have a child after the deadline then you may add them to your plan within 30 days of the event.
                    </li>
                </ul>
                <p>
                    For more information please see
                    the <a href='http://www.washington.edu/ship/international-student-insurance-health-plan/iship-faqs/' target='_blank'>International
                    Student Insurance FAQ</a> or go to {moreInfoLocation}
                </p>
                <p>
                    Summer Quarter - If you will NOT be registered for courses during summer quarter, you may purchase coverage
                    by selecting annual coverage when you register for courses during autumn, winter or spring quarters.
                </p>
            </React.Fragment>
        );

        const statusChar = data.ins_status_char;

        // Graduate students
        if (parseInt(data.class_code) === 8) {
            const isTaRaStudent = data.ta_ra_sw === "Y";

            if ( isAcademicYearGAIP || ( statusChar === "W" && isTaRaStudent )) {
                // summer grad student TA/RA with aut/win/spr disbursed TA/RA Interface awards, or
                // graduate student TA/RA with student insurance waiver

                if ( statusChar === "W" && isTaRaStudent ) {
                    switch (parseInt(data.curr_ins_sel_code)) {
                        case 0: // No insurance
                            if (! isAcademicYearGAIP) {
                                component.push(
                                    <p key={guid()}>You qualify for the {gsGAIP}.</p>
                                )
                            }
                            break;
                        case 1:
                        case 5: // Student-only insurance
                            component.push(
                                <p key={guid()}>Your enrollment in the {gsSASIP} has been cancelled because you qualify for the {gsGAIP}.</p>
                            );
                            break;
                        default:
                            component.push(
                                <p key={guid()}>
                                    Your enrollment in the {gsSASIP} has been cancelled because you qualify for the {gsGAIP}.
                                    However, you must re-enroll dependents in the {gsGAIP}.
                                </p>
                            );
                    }
                }

                if ( isAcademicYearGAIP ) {
                    component.push(
                        <p key={guid()}>
                            <strong>It appears you have participated in the {gsGAIP} for the preceding three quarters and are
                                therefore eligible for UW-paid GAIP insurance for summer quarter, regardless of your student and/or employment status.</strong>
                        </p>
                    );
                }

                if (this.props.showButton) {
                    component.push(<Button onClick={this.props.onSuccess} key={guid()}>Continue</Button>);
                }

                return component;
            } else {
                component.push(
                    <ul key={guid()}>
                        <li>
                            <strong>Graduate Appointee Insurance Program:</strong> If you have a qualifying TA/RA/SA or fellow/trainee appointment for {termName},
                            you may be eligible for the {gsGAIP} (GAIP). If you are eligible for GAIP your ISHIP coverage will be cancelled automatically. Please note
                            that you will need to re-enroll your spouse and children in GAIP.
                        </li>
                    </ul>
                );
            }
        }

        const isUpdatable = data.ins_update_sw === "Y";
        const isEnrollmentRequired = data.enroll_requird_sw === "Y";

        if (data.ins_status_char === "C" && parseInt(data.curr_ins_sel_code) > 0 && (parseInt(data.ins_cancel_reason) !== 1 && parseInt(data.ins_cancel_reason) !== 2)) {
            component.push(
                this.insuranceCancelledDescription()
            )

            if (this.props.showButton) {
                component.push(<Button onClick={this.props.onSuccess} key={guid()}>Continue</Button>);
            }

            return component;
        }

        if (! isUpdatable) {
            if (parseInt(data.curr_ins_sel_code) === 0) {
                component.push(
                    <p key={guid()}>It is past the deadline to enroll in the Student Health Insurance Plan for {termName}.</p>
                )

                if (this.props.showButton) {
                    component.push(<Button onClick={this.props.onSuccess} key={guid()}>Continue</Button>);
                }

                return component;
            }

            component.push(
                <p key={guid()}>
                    It is past the deadline to withdraw from the {gsSASIP} or change your coverage for {termName}.
                </p>
            )

        }

        component.push(
            <strong key={guid()}>International Student Health Insurance Plan:</strong>
        )

        if (
            isEnrollmentRequired
            ||
            (
                statusChar === "C"
                &&
                parseInt(data.curr_ins_sel_code) > 0
                &&
                (
                    parseInt(data.ins_cancel_reason) === 1
                    ||
                    parseInt(data.ins_cancel_reason) === 2
                )
            )
        ) {
            component.push(
                <Alert variant="danger" key={guid()}>Your insurance is not active unless you register for class(es) and remain registered through the 3rd Friday of {termName} ({tuitionDueDate})</Alert>
            );
        }

        if (
            isUpdatable
            ||
            data.has_annual_ins_sw === "Y"
            ||
            data.ins_status_char === "W"
        ) {
            //...Student will not be given the radio buttons for updating insurance
            component.push(
                <React.Fragment key={guid()}>{this.insuranceCurrentDescription()}</React.Fragment>
            )
        } else {
            component.push(
                <React.Fragment key={guid()}>{this.insuranceDisplayUpdateButtons()}</React.Fragment>
            )
        }

        if (this.props.showButton) {
            component.push(<Button onClick={this.props.onSuccess} key={guid()}>Continue</Button>);
        }

        return component;
    }
}

OpChargeInsurance.propTypes = {
    campus: PropTypes.string.isRequired,
    termName: PropTypes.string.isRequired,
    termId: PropTypes.string.isRequired,
    showButton: PropTypes.bool
}

OpChargeInsurance.defaultProps = {
    showButton: false
}


export default observer(OpChargeInsurance);